import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { CiteThis } from "@/components/site/CiteThis";
import { JUSTIFICATIONS } from "@/lib/reference/justifications";
import { SITE } from "@/lib/site";

export const Route = createFileRoute("/tools/justifications/")({
  head: () => {
    const url = `${SITE.url}/tools/justifications`;
    return {
      meta: [
        { title: `Map Pack Justifications Decoded — Reviews, Posts, Website, Menu | ${SITE.name}` },
        {
          name: "description",
          content:
            "Every type of Google Map Pack justification — review snippets, website mentions, posts, menu matches, in-stock — explained with examples and how to earn each one.",
        },
        { property: "og:title", content: `Justification Decoder | ${SITE.name}` },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Map Pack justification types",
            url,
            itemListElement: JUSTIFICATIONS.map((j, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: j.name,
              url: `${url}/${j.slug}`,
            })),
          }),
        },
      ],
    };
  },
  component: JustificationsIndex,
});

function JustificationsIndex() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-4xl px-5 sm:px-8 py-16">
        <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-wider text-muted-foreground">
          <ol className="flex items-center gap-2 flex-wrap">
            <li><Link to="/" className="hover:text-accent">Home</Link></li>
            <li aria-hidden>/</li>
            <li><Link to="/tools" className="hover:text-accent">Tools</Link></li>
            <li aria-hidden>/</li>
            <li>Justifications</li>
          </ol>
        </nav>
        <header className="mt-6 max-w-3xl">
          <p className="eyebrow">Reference</p>
          <h1 className="headline-display mt-3 text-5xl">Map Pack justification decoder</h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Justifications are the snippet Google attaches under your listing to explain why you ranked. Earn the right ones and your CTR climbs without changing position.
          </p>
        </header>

        <div className="mt-8 flex items-center gap-3 border-t border-b border-border py-4">
          <CiteThis title="Map Pack Justification Decoder" url={`${SITE.url}/tools/justifications`} />
        </div>

        <ul className="mt-10 space-y-4">
          {JUSTIFICATIONS.map((j) => (
            <li key={j.slug}>
              <Link
                to="/tools/justifications/$slug"
                params={{ slug: j.slug }}
                className="block rounded-sm border border-border bg-paper-soft p-5 hover:border-foreground hover:bg-background transition-colors"
              >
                <h2 className="text-xl font-semibold">{j.name}</h2>
                <p className="mt-1 text-sm italic text-muted-foreground">{j.example}</p>
                <p className="mt-2 text-sm text-foreground/80">{j.description}</p>
              </Link>
            </li>
          ))}
        </ul>
      </main>
      <SiteFooter />
    </div>
  );
}
